import { CategoryIcons } from '@/components/icons/CategoryIcons';
import { SpecIcons } from '@/components/icons/SpecIcons';
import { Picture } from '@/components/image/Image';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import InfoTooltip from '@/components/tooltip/InfoTooltip';
import SiTrendingUpFill from '@/icons/SiTrendingUpFill';
import { Product } from '@/services/api/contracts/catalog/types';
import { RoleType } from '@/services/authentication/hooks/user';
import { BasicBetterBest } from '@/services/catalogue/useBasicBetterBest';
import { useCatalogueService } from '@/services/catalogue/useCatalogueService';
import { FC, PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';
import { PriceBreakdown } from '../../../../pages/product/price/PriceHistoryModal';
import ProductMarginHoverSection from '../ProductMarginHoverSection';
import ProductCardCustomerContent from './ProductCardCustomerContent';

interface Props {
  product: Product;
  priceHistoryModalProps?: ModalProps<{ productId: string }>;
}

interface ProductImageProps {
  product: Product;
}

export const ProductImage: FC<ProductImageProps> = ({ product }) => {
  return product.imageUri?.id ? (
    <div className="w-full h-[150px] flex items-center justify-center">
      <Picture image={product.imageUri} containerHeight={125} containerWidth={250}></Picture>
    </div>
  ) : (
    <div className="w-full h-[150px] [&_svg]:w-[100%] [&_svg]:h-[100%]">
      {CategoryIcons[product.category.description]}
    </div>
  );
};

interface ProductContentProps {
  product: Product;
  quantity?: number;
}

export const ProductContent: FC<ProductContentProps> = ({ product, quantity }) => {
  const [configuration] = useCatalogueService(state => [state.configuration]);

  return (
    <div className={'flex flex-1 flex-col gap-1 px-4 py-3'}>
      <p className="text-lg font-semibold">{quantity !== undefined ? `${product.name} X ${quantity}` : product.name}</p>
      {configuration.for === RoleType.Admin && (
        <div className="text-sm leading-tight font-semibold">{`Region: ${[product.region.name]}`}</div>
      )}
      {product.propertyBag.basicBetterBest && product.propertyBag.basicBetterBest !== BasicBetterBest.UNSET && (
        <div className="flex gap-1 items-start text-secondary-500">
          <div>
            Included in{' '}
            <span className={product.propertyBag.basicBetterBest === BasicBetterBest.BASIC ? 'font-bold' : ''}>
              basic
            </span>{' '}
            <span className={product.propertyBag.basicBetterBest === BasicBetterBest.BETTER ? 'font-bold' : ''}>
              better
            </span>{' '}
            <span className={product.propertyBag.basicBetterBest === BasicBetterBest.BEST ? 'font-bold' : ''}>
              best
            </span>
            .
          </div>
          <InfoTooltip
            id={`bbb-${product.id}`}
            text={"This stock is held by DaaS, so you don't have to manage the stock levels."}
          />
        </div>
      )}
      <div className="flex flex-wrap gap-1">
        {product.propertyBag.properties?.map(spec => (
          <span className="flex items-center gap-1" key={spec.text}>
            {spec.iconId ? (
              <span className="text-2xl flex items-center justify-center">{SpecIcons[spec.iconId]}</span>
            ) : (
              <span className="text-sm opacity-70">•</span>
            )}
            <span className="text-sm font-medium">{spec.text}</span>
          </span>
        ))}
      </div>
      <div className="text-sm font-bold mt-1">
        SKU: {product.productId}
      </div>
    </div>
  );
};

export const ProductCard: FC<PropsWithChildren<Props>> = ({ product, priceHistoryModalProps, children }) => {
  const latestMarginApplication = product.price?.margins.at(-1);

  const [configuration] = useCatalogueService(state => [state.configuration]);

  return (
    <Card
      className={twMerge(
        'flex flex-col items-stretch overflow-clip',
        product.propertyBag.basicBetterBest && product.propertyBag.basicBetterBest !== BasicBetterBest.UNSET
          ? 'border-4 border-secondary-500'
          : ''
      )}
    >
      <div className="flex border-b w-full">
        <ProductImage product={product} />
      </div>
      <ProductContent product={product} />
      {configuration.for === RoleType.Company && <ProductCardCustomerContent product={product} />}
      {product.price && priceHistoryModalProps && (
        <div className="border-secondary-std group flex items-center justify-between gap-2 border-t bg-[#F3FCFE] px-4 py-3">
          <PriceBreakdown
            date={new Date(product.price.date)}
            percentages={product.price.margins}
            price={product.price.price}
            categoryId={product.category.id}
            manufacturerId={product.manufacturer.id}
            regionCode={product.region?.code}
          />
          <div className="flex gap-1 flex-col">
            {latestMarginApplication && <ProductMarginHoverSection breakdownMargin={latestMarginApplication} />}
            <button
              onClick={() =>
                priceHistoryModalProps.open({
                  productId: product.id
                })
              }
              className="flex items-center justify-center rounded-full bg-[#DDEDF0] p-1"
            >
              <SiTrendingUpFill className="text-xl" />
            </button>
          </div>
        </div>
      )}
      {children}
    </Card>
  );
};
